import { InjectionToken, type Signal } from '@angular/core';
import type { TeamRole } from '../company/company-team-member.model';
import type { Company } from '../company/company.model';
import type { User } from '../user/user';

/**
 * Abstraction over the current application session.
 *
 * Shared libraries MUST consume this token instead of reaching into a specific
 * auth implementation (e.g. `AuthSessionStore` in `@indutrust/auth-data`).
 * Each brand's `config` library is responsible for providing `APP_SESSION`
 * with a concrete implementation wired to the brand's authentication strategy.
 *
 * This keeps `scope:shared` libraries free of any `scope:domain` coupling
 * while still allowing shell components, shared stores and test harnesses
 * to react to authentication state.
 *
 * @example
 * // In a brand config provider:
 * {
 *   provide: APP_SESSION,
 *   useFactory: (): AppSession => {
 *     const store = inject(AuthSessionStore);
 *     const dispatch = injectDispatch(authSessionEvents);
 *     return {
 *       user: store.user,
 *       company: store.company,
 *       isAuthenticated: store.isAuthenticated,
 *       teamRole: store.teamRole,
 *       logout: () => dispatch.logoutRequested(),
 *     };
 *   },
 * }
 */
export type AppSession = {
  /** Currently signed-in user, or `null`/`undefined` when not authenticated. */
  user: Signal<User | null | undefined>;
  /** Company associated with the current user, or `null`/`undefined`. */
  company: Signal<Company | null | undefined>;
  /** Whether a valid session is currently active. */
  isAuthenticated: Signal<boolean>;
  /**
   * Resolved intra-Company sub-role for the signed-in user (`'owner'`
   * when the backend omits `team_role`).
   */
  teamRole: Signal<TeamRole>;
  /** Requests logout on the underlying auth implementation. */
  logout: () => void;
};

/**
 * Injection token for the current {@link AppSession}.
 *
 * Provided by each brand's `config` library so that shared libraries
 * never need to import `@indutrust/auth-data` directly.
 */
export const APP_SESSION = new InjectionToken<AppSession>('APP_SESSION');
